import { useEffect, useLayoutEffect, useState } from 'preact/hooks'
import { AthosRecommendProvider } from '../fetchers/recommend'
import { CarouselResults } from './CarouselResults'
import clsx from 'clsx'
import { BeaconAnalyticsProvider } from '../hooks/useBeaconAnalytics'
import { dataTracker } from '@/components/snippets/analytics/analytics'

// Cards are ~1.2 across below 1024px; desktop keeps the default 50vw.
const CARD_IMAGE_SIZES = '(min-width: 1024px) 50vw, 85vw'

export function CarouselTabbed({ values, itemClass, productId, idList }) {
  const ids = idList.split(',')
  const profiles = values.split(',').map((value, i) => {
    const [profile, label] = value.split(':')
    const id = ids[i]
    return {
      profile,
      label,
      id
    }
  })

  const [activeProfile, setActiveProfile] = useState(profiles[0])
  const [loadedProfiles, setLoadedProfiles] = useState(new Set([profiles[0].profile]))

  useEffect(() => {
    const analyticsProfile = dataTracker.getSectionDataById(activeProfile.id)
    if (analyticsProfile) {
      dataTracker.replaceSectionDataById(activeProfile.id, analyticsProfile)
    }
  }, [activeProfile])

  // Flickity's measurements go stale while a panel is display:none; re-measure before the
  // shown panel paints, or its first frame renders the stale layout and then snaps.
  useLayoutEffect(() => {
    const carousel = document.getElementById(activeProfile.id)?.querySelector('x-flickity')
    if (!carousel?.flickity) return
    carousel.runWithNaturalCellHeights(() => carousel.flickity.resize())
  }, [activeProfile])

  const handleProfileChange = (item) => {
    setLoadedProfiles(prev => new Set([...prev, item.profile]))
    setActiveProfile(item)
  }

  return (
    <div className="flex flex-col gap-6 md:gap-8">
      <div className="relative z-10 flex  gap-2 flex-col md:flex-row md:gap-4 w-fit">
        {profiles.map((item) => (
          <button
            key={item.profile}
            className={clsx(
              'h2 border-b pb-1 text-left',
              item.profile === activeProfile?.profile
                ? 'border-primary-blue text-primary-blue'
                : 'border-transparent text-neutrals-secondary-grey'
            )}
            onClick={() => handleProfileChange(item)}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div style="--translate-y-18:-4.5rem;" className="md:translate-y-[--translate-y-18]">
        {profiles.map((item) => (
          loadedProfiles.has(item.profile) && (
            <div
              key={item.profile}
              id={item.id}
              data-dl-section-id={item.profile}
              data-dl-section-name={item.label}
              // display:none, not h-0: WebKit keeps the collapsed clip on Flickity's composited slider
              // and paints only a strip of each card when the panel is shown again.
              className={item.profile !== activeProfile.profile ? 'hidden' : 'animate-fade-in'}
            >
              <AthosRecommendProvider profile={item.profile} productId={productId}>
                <BeaconAnalyticsProvider type="recommendations" profile={item.profile}>
                  <CarouselResults
                    className={itemClass}
                    hideControlsOnMobile={true}
                    beaconEnabled={item.profile === activeProfile?.profile}
                    imageSizes={CARD_IMAGE_SIZES}
                  />
                </BeaconAnalyticsProvider>
              </AthosRecommendProvider>
            </div>
          )
        ))}
      </div>
    </div>
  )
}
